import React, { useState, useEffect } from "react";
import apiClient from "../apiClient";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useShop } from "../context/ShopContext";
import { FaSpinner } from "react-icons/fa";
import EmptyState from "./EmptyState";
import "./responsiveTable.css";

const OpeningStockEntry = () => {
  const { shopId } = useShop();
  const [typeId, setTypeId] = useState("1"); // 1 = English, 2 = Desi — same TypeId convention used across the app
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [products, setProducts] = useState([]);
  const [rows, setRows] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const showToast = (message, type = "error") => {
    toast.dismiss();
    if (type.toLowerCase() === "error") toast.error(message);
    else if (type.toLowerCase() === "success") toast.success(message);
  };

  const fetchProducts = async () => {
    if (!shopId) return;
    try {
      setLoading(true);
      const response = await apiClient.post("/Liquor/GetProductMasterForOpeningStock", {
        shopId,
        typeId,
      });

      if (response.data?.code === 200 && Array.isArray(response.data.data)) {
        const formatted = response.data.data.map((p) => ({
          id: p.id,
          pName: p.pName,
          packing: p.packing,
          mrp: parseFloat(p.mrp) || 0,
          currentStock: parseFloat(p.opening) || 0,
          sortOrder: parseInt(p.orderNo) || 0,
        }));
        setProducts(formatted);

        const initialRows = formatted.reduce((acc, p) => {
          acc[p.id] = { opeQty: "", orderNo: p.sortOrder > 0 ? String(p.sortOrder) : "" };
          return acc;
        }, {});
        setRows(initialRows);
      } else {
        showToast(response.data?.message || "Failed to fetch products", "error");
        setProducts([]);
      }
    } catch (error) {
      showToast(error.response?.data?.message || "Error fetching products", "error");
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopId, typeId]);

  const handleRowChange = (id, field, value) => {
    if (value !== "" && (isNaN(value) || Number(value) < 0)) return;
    setRows((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const enteredCount = Object.values(rows).filter((r) => r.opeQty && Number(r.opeQty) > 0).length;

  const handleSubmit = async () => {
    const entered = products.filter((p) => rows[p.id]?.opeQty && Number(rows[p.id].opeQty) > 0);

    if (entered.length === 0) {
      showToast("Enter an opening quantity for at least one product", "error");
      return;
    }

    const missingOrder = entered.filter((p) => !rows[p.id]?.orderNo || Number(rows[p.id].orderNo) <= 0);
    if (missingOrder.length > 0) {
      showToast(`Set a Sort Order for: ${missingOrder.map((p) => p.pName).join(", ")}`, "error");
      return;
    }

    const orderNos = entered.map((p) => Number(rows[p.id].orderNo));
    const dupes = orderNos.filter((n, i) => orderNos.indexOf(n) !== i);
    if (dupes.length > 0) {
      showToast(`Duplicate Sort Order number(s): ${[...new Set(dupes)].join(", ")}`, "error");
      return;
    }

    const payload = {
      ShopId: shopId,
      TypeId: typeId,
      OpeningDate: date,
      Products: entered.map((p) => ({
        ProductId: p.id,
        ProductName: p.pName,
        Packing: p.packing,
        MRP: p.mrp,
        OpeQty: Number(rows[p.id].opeQty),
        OrderNo: Number(rows[p.id].orderNo),
      })),
    };

    try {
      setSaving(true);
      const response = await apiClient.post("/Liquor/SaveOpeningStock", payload);
      if (response.data?.code === 200) {
        showToast("Opening stock saved successfully!", "success");
        fetchProducts();
      } else {
        showToast(response.data?.message || "Failed to save opening stock", "error");
      }
    } catch (error) {
      showToast(error.response?.data?.message || "Error saving opening stock", "error");
    } finally {
      setSaving(false);
    }
  };

  const filteredProducts = products.filter((p) =>
    (p.pName || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-gray-900 fw-bold fs-3 flex-column justify-content-center my-0">
                Opening Stock Entry
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">Home</li>
                <li className="breadcrumb-item"><span className="bullet bg-gray-500 w-5px h-2px"></span></li>
                <li className="breadcrumb-item text-muted">Opening Stock</li>
              </ul>
            </div>
          </div>
        </div>

        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-fluid">
            <div className="row g-5 g-xl-10 mb-5 mb-xl-10">
              <div className="col-xl-12">
                <div className="card card-flush h-md-100">
                  <ToastContainer />
                  <div className="card-body pt-6">
                    <div className="table-responsive">
                      <div className="panel-body">

                        <div className="form-group d-flex gap-5 mb-4 flex-wrap">
                          <div className="col-lg-3 col-6">
                            <label className="form-label">Opening Date</label>
                            <input
                              type="date"
                              className="form-control"
                              value={date}
                              max={new Date().toISOString().split("T")[0]}
                              onChange={(e) => setDate(e.target.value)}
                            />
                          </div>
                          <div className="col-lg-3 col-6">
                            <label className="form-label">Type</label>
                            <select className="form-select" value={typeId} onChange={(e) => setTypeId(e.target.value)}>
                              <option value="1">अंग्रेजी (English)</option>
                              <option value="2">देशी (Desi)</option>
                            </select>
                          </div>
                          <div className="col-lg-3 col-6">
                            <label className="form-label">Search Product</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Search product"
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                            />
                          </div>
                        </div>

                        <div className="clearfix"></div>
                        <br />

                        {/* Desktop table */}
                        <div className="table-responsive h-700px entry-desktop">
                          {loading ? (
                            <div className="loader-container"><FaSpinner className="spinner-icon" /></div>
                          ) : filteredProducts.length > 0 ? (
                            <table className="table table-bordered table-striped">
                              <thead>
                                <tr>
                                  <th>S.No.</th>
                                  <th>Product Name</th>
                                  <th>Packing</th>
                                  <th>MRP</th>
                                  <th>Current Stock</th>
                                  <th>Sort Order</th>
                                  <th>Opening Qty</th>
                                </tr>
                              </thead>
                              <tbody>
                                {filteredProducts.map((product, index) => (
                                  <tr key={product.id}>
                                    <td>{index + 1}</td>
                                    <td>{product.pName}</td>
                                    <td>{product.packing}</td>
                                    <td>{product.mrp}</td>
                                    <td>{product.currentStock}</td>
                                    <td>
                                      <input
                                        type="number" min="1" className="form-control"
                                        style={{ width: 90 }}
                                        placeholder="0"
                                        value={rows[product.id]?.orderNo || ""}
                                        onChange={(e) => handleRowChange(product.id, "orderNo", e.target.value)}
                                      />
                                    </td>
                                    <td>
                                      <input
                                        type="number" min="0" className="form-control"
                                        style={{ width: 90 }}
                                        placeholder="0"
                                        value={rows[product.id]?.opeQty || ""}
                                        onChange={(e) => handleRowChange(product.id, "opeQty", e.target.value)}
                                      />
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <EmptyState />
                          )}
                        </div>

                        {/* Mobile: product cards */}
                        <div className="entry-mobile">
                          {loading ? (
                            <div className="loader-container"><FaSpinner className="spinner-icon" /></div>
                          ) : filteredProducts.length === 0 ? (
                            <EmptyState />
                          ) : (
                            filteredProducts.map((product) => (
                              <div key={product.id} className="ef-card">
                                <div className="ef-card-header">
                                  <span className="ef-card-title">{product.pName}</span>
                                  <span className="ef-card-badge">{product.packing}</span>
                                </div>
                                <div className="ef-card-body">
                                  <div className="ef-info-row">
                                    <div className="ef-info-pill">
                                      <span className="ef-info-pill-label">MRP</span>
                                      <span className="ef-info-pill-value">{product.mrp}</span>
                                    </div>
                                    <div className="ef-info-pill">
                                      <span className="ef-info-pill-label">Stock</span>
                                      <span className="ef-info-pill-value">{product.currentStock}</span>
                                    </div>
                                  </div>
                                  <div className="ef-field">
                                    <span className="ef-field-label">Sort Order</span>
                                    <input
                                      type="number" min="1" className="form-control"
                                      placeholder="0"
                                      value={rows[product.id]?.orderNo || ""}
                                      onChange={(e) => handleRowChange(product.id, "orderNo", e.target.value)}
                                    />
                                  </div>
                                  <div className="ef-field">
                                    <span className="ef-field-label">Opening Qty</span>
                                    <input
                                      type="number" min="0" className="form-control"
                                      placeholder="0"
                                      value={rows[product.id]?.opeQty || ""}
                                      onChange={(e) => handleRowChange(product.id, "opeQty", e.target.value)}
                                    />
                                  </div>
                                </div>
                              </div>
                            ))
                          )}
                        </div>

                        {/* Summary + Submit bar */}
                        <div style={{
                          display: "flex", alignItems: "center", justifyContent: "space-between",
                          flexWrap: "wrap", gap: 12, marginTop: 16,
                          padding: "14px 18px",
                          background: "linear-gradient(135deg, #3D0D26 0%, #5E1540 60%, #7B1D4F 100%)",
                          borderRadius: 12,
                        }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#10B981", flexShrink: 0 }} />
                            <span style={{ fontSize: 12, color: "rgba(255,255,255,0.65)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Entered</span>
                            <span style={{ fontSize: 17, fontWeight: 900, color: "#fff" }}>{enteredCount}</span>
                          </div>
                          <button
                            className="btn btn-primary"
                            onClick={handleSubmit}
                            disabled={enteredCount === 0 || saving}
                            style={{ minWidth: 130, fontWeight: 700 }}
                          >
                            {saving ? "Saving…" : "✓ Save Opening Stock"}
                          </button>
                        </div>

                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpeningStockEntry;
